import { useState } from "react";
import type { MatrixClient } from "matrix-js-sdk";
import { Avatar, Spinner, Text } from "../../theme/primitives";
import { startDirectMessage } from "../../matrix/rooms";
import { useUserSearch } from "./useUserSearch";
import type { UserSearchResult } from "../../api/types";

interface NewChatModalProps {
  client: MatrixClient;
  token: string;
  onClose: () => void;
  onStarted: (roomId: string) => void;
}

/** Modal to search users by email/name and start a 1:1 DM. */
export function NewChatModal({ client, token, onClose, onStarted }: NewChatModalProps) {
  const [query, setQuery] = useState("");
  const [startingId, setStartingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const search = useUserSearch(query, token);

  const start = async (user: UserSearchResult) => {
    setStartingId(user.matrixUserId);
    setError(null);
    try {
      const roomId = await startDirectMessage(client, user.matrixUserId);
      onStarted(roomId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start the chat.");
      setStartingId(null);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "var(--color-overlay)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--space-lg)",
        zIndex: 10,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(440px, 100%)",
          maxHeight: "80vh",
          background: "var(--color-panel)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-md)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "var(--space-lg)",
            borderBottom: "1px solid var(--color-border)",
          }}
        >
          <Text weight="bold" size="lg">
            New chat
          </Text>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: "transparent", border: "none", fontSize: 18 }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: "var(--space-lg)" }}>
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by email or name"
            aria-label="Search users"
            style={{
              width: "100%",
              padding: "var(--space-sm) var(--space-md)",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--color-border)",
              fontSize: "var(--font-size-md)",
              outline: "none",
            }}
          />
        </div>

        {error && (
          <div style={{ padding: "0 var(--space-lg) var(--space-md)" }}>
            <Text style={{ color: "var(--color-danger)" }} size="sm">
              {error}
            </Text>
          </div>
        )}

        <div style={{ overflowY: "auto", flex: 1 }}>
          {search.status === "loading" && (
            <div style={{ display: "flex", justifyContent: "center", padding: "var(--space-lg)" }}>
              <Spinner size={22} />
            </div>
          )}
          {search.status === "error" && (
            <div style={{ padding: "var(--space-lg)", textAlign: "center" }}>
              <Text style={{ color: "var(--color-danger)" }}>{search.error}</Text>
            </div>
          )}
          {search.status === "success" && search.results.length === 0 && (
            <div style={{ padding: "var(--space-lg)", textAlign: "center" }}>
              <Text muted>No people found.</Text>
            </div>
          )}
          {search.status === "idle" && query.trim().length < 2 && (
            <div style={{ padding: "var(--space-lg)", textAlign: "center" }}>
              <Text muted size="sm">
                Type at least 2 characters to search.
              </Text>
            </div>
          )}
          {search.results.map((user) => (
            <button
              key={user.matrixUserId}
              onClick={() => start(user)}
              disabled={startingId !== null}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--space-md)",
                width: "100%",
                border: "none",
                borderBottom: "1px solid var(--color-border)",
                background: "transparent",
                padding: "var(--space-md) var(--space-lg)",
                textAlign: "left",
              }}
            >
              <Avatar name={user.displayName || user.email} size={40} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text weight="medium" truncate>
                  {user.displayName || user.email}
                </Text>
                <br />
                <Text muted size="sm" truncate>
                  {user.email}
                </Text>
              </div>
              {startingId === user.matrixUserId && <Spinner size={18} />}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
